import React from 'react';
import {View} from 'react-native';

import FormattedNumber from '/component/base/FormattedNumber';
import Text from '/component/base/Text';
import {CoachInfoCard} from '/constant/TestIDs';
import {withTranslation} from '/provider/TranslationProvider';
import {withHOC} from '/util/hoc.util';

import {styles} from './Volume.style';

// Define the types for the props
interface VolumeProps {
  tt: (key: string, fallback: string) => string;
  reportRecordData: {
    volume: {
      fqv: number;
      gen0Qv: number;
      gqv: number;
      pqv: number;
    };
    teamCounts: {
      seniorCoachTeamsCount: number;
      executiveDirectorTeamsCount: number;
      fibcTeamsCount: number;
      fiblTeamsCount: number;
    };
  };
}

export const Volume: React.FC<VolumeProps> = ({
  tt = (key: string, fallback: string) => fallback,
  reportRecordData = {
    volume: {
      fqv: 0,
      gen0Qv: 0,
      gqv: 0,
      pqv: 0,
    },
    teamCounts: {
      seniorCoachTeamsCount: 0,
      executiveDirectorTeamsCount: 0,
      fibcTeamsCount: 0,
      fiblTeamsCount: 0,
    },
  },
}) => {
  const {
    volume: {fqv = 0, gen0Qv = 0, gqv = 0, pqv = 0} = {},
    teamCounts: {
      seniorCoachTeamsCount = 0,
      executiveDirectorTeamsCount = 0,
      fibcTeamsCount = 0,
      fiblTeamsCount = 0,
    } = {},
  } = reportRecordData || {};

  return (
    <View style={styles.footer}>
      <View style={styles.footerGroup}>
        <View accessible={true} accessibilityLabel={`Volume statistics`}>
          <Text
            testID={CoachInfoCard.volumeTitle}
            emphasis="standard"
            variant="subheading"
            style={styles.footerGroupTitle}>
            {tt('footer.volume.label', 'Volume')}
          </Text>
        </View>

        <View style={styles.footerRow} accessible={true}>
          <Text variant="button">{tt('footer.pqv.label', 'PQV')}</Text>
          <FormattedNumber
            testID={CoachInfoCard.pqv}
            textEmphasis="medium"
            boldText={false}
            value={pqv}
            truncate
            preserveNegative
          />
        </View>
        <View style={styles.footerRow} accessible={true}>
          <Text variant="button">{tt('footer.fqv.label', 'FQV')}</Text>
          <FormattedNumber
            testID={CoachInfoCard.fqv}
            textEmphasis="medium"
            boldText={false}
            value={fqv}
            truncate
            preserveNegative
          />
        </View>
        <View style={styles.footerRow} accessible={true}>
          <Text variant="button">{tt('footer.gqv.label', 'GQV')}</Text>
          <FormattedNumber
            testID={CoachInfoCard.gqv}
            textEmphasis="medium"
            boldText={false}
            value={gqv}
            truncate
            preserveNegative
          />
        </View>
        <View style={styles.footerRow} accessible={true}>
          <Text variant="button">{tt('footer.gen0qv.label', 'GEN0 QV')}</Text>
          <FormattedNumber
            testID={CoachInfoCard.gen0qv}
            textEmphasis="medium"
            boldText={false}
            value={gen0Qv}
            truncate
            preserveNegative
          />
        </View>
      </View>

      <View style={styles.footerGroup}>
        <View accessible={true} accessibilityLabel={`Teams statistics`}>
          <Text
            testID={CoachInfoCard.teamsTitle}
            emphasis="standard"
            variant="subheading"
            style={styles.footerGroupTitle}>
            {tt('footer.teams.label', 'Teams')}
          </Text>
        </View>

        <View
          style={styles.footerRow}
          accessible={true}
          accessibilityLabel={`SC ${seniorCoachTeamsCount}`}>
          <Text variant="button">{tt('footer.sc.label', 'SC')}</Text>
          <Text emphasis="medium" testID={CoachInfoCard.sc}>
            {seniorCoachTeamsCount}
          </Text>
        </View>
        <View
          style={styles.footerRow}
          accessible={true}
          accessibilityLabel={`ED ${executiveDirectorTeamsCount}`}>
          <Text variant="button">{tt('footer.ed.label', 'ED')}</Text>
          <Text emphasis="medium" testID={CoachInfoCard.ed}>
            {executiveDirectorTeamsCount}
          </Text>
        </View>
        <View
          style={styles.footerRow}
          accessible={true}
          accessibilityLabel={`FIBC ${fibcTeamsCount}`}>
          <Text variant="button">{tt('footer.fibc.label', 'FIBC')}</Text>
          <Text emphasis="medium" testID={CoachInfoCard.fibc}>
            {fibcTeamsCount}
          </Text>
        </View>
        <View
          style={styles.footerRow}
          accessible={true}
          accessibilityLabel={`FIBL ${fiblTeamsCount}`}>
          <Text variant="button">{tt('footer.fibl.label', 'FIBL')}</Text>
          <Text emphasis="medium" testID={CoachInfoCard.fibl}>
            {fiblTeamsCount}
          </Text>
        </View>
      </View>
    </View>
  );
};

export default withHOC(withTranslation('reportCard.coach'))(Volume);
